import {
  CompositeFilterDescriptor,
  DataResult,
  DataSourceRequestState
} from '@progress/kendo-data-query';
import {
  GridColumn as Column,
  Grid,
  GridSortChangeEvent
} from '@progress/kendo-react-grid';
import React, { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useColumns } from 'src/components/shared/DataGrid/ColumnMenu';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { DateTimeCell } from 'src/components/shared/DataGrid/DateCell';
import { ParameterSetDetailDto } from 'src/store/parametersets/parameterset';
import { ProductDto } from 'src/store/products/product';
import { getCorrectedValueAtIndexFormatted } from 'src/store/products/product.utils';
import { productListColumnWidths } from './ProductListColumnWidths';
import { getProductColumns } from './ProductsListColumnMenu';

type Props = {
  data: DataResult;
  loading: boolean;
  requestState: DataSourceRequestState;
  parameterSet: Nullable<ParameterSetDetailDto>;
  onEdit: (product: ProductDto) => void;
  onSortChange: (event: GridSortChangeEvent) => void;
  onFilterChange: (filter: CompositeFilterDescriptor | undefined) => void;
  onPageChange: (skip: number, take: number) => void;
  gridRef: RefObject<Grid>;
};

export const ProductParametersList: React.FC<Props> = props => {
  const {
    data,
    loading,
    requestState,
    parameterSet,
    onEdit,
    onSortChange,
    onFilterChange,
    onPageChange,
    gridRef
  } = props;

  const { t } = useTranslation();

  const { columns, isColumnVisible } = useColumns<ProductDto>(
    getProductColumns()
  );

  return (
    <DataGrid
      ref={gridRef}
      data={data}
      loading={loading}
      pageable
      filterable={false}
      filter={requestState.filter}
      sort={requestState.sort}
      skip={requestState.skip}
      take={requestState.take}
      pageSize={20}
      onSortChange={onSortChange}
      onFilterChange={event => {
        onFilterChange(event.filter);
      }}
      onPageChange={onPageChange}
      onRowClick={({ dataItem }) => onEdit(dataItem)}
    >
      {isColumnVisible(columns, 'runNo') && (
        <Column
          field="runNo"
          title={t('product.runNo')}
          width={productListColumnWidths['runNo']}
        />
      )}
      {isColumnVisible(columns, 'runSystemName') && (
        <Column
          field="runSystemName"
          title={t('runSystem.singular')}
          width={productListColumnWidths['runSystemName']}
        />
      )}
      {isColumnVisible(columns, 'runDate') && (
        <Column
          field="runDate"
          title={t('product.runDate')}
          cell={props => <DateTimeCell {...props} />}
          width={productListColumnWidths['runDate']}
        />
      )}
      {isColumnVisible(columns, 'runText') && (
        <Column
          field="runText"
          title={t('product.runText')}
          width={productListColumnWidths['runText']}
        />
      )}
      {isColumnVisible(columns, 'lotText') && (
        <Column
          field="lotText"
          title={t('product.lotText')}
          width={productListColumnWidths['lotText']}
        />
      )}
      {isColumnVisible(columns, 'gradeName') && (
        <Column
          field="gradeName"
          title={t('grade.singular')}
          width={productListColumnWidths['gradeName']}
        />
      )}
      {isColumnVisible(columns, 'productTypeName') && (
        <Column
          field="productTypeName"
          title={t('productType.singular')}
          width={productListColumnWidths['productTypeName']}
        />
      )}
      {isColumnVisible(columns, 'segmentName') && (
        <Column
          field="segmentName"
          title={t('segment.singular')}
          width={productListColumnWidths['segmentName']}
        />
      )}
      {isColumnVisible(columns, 'lotNo') && (
        <Column
          field="lotNo"
          title={t('product.lotNo')}
          width={productListColumnWidths['lotNo']}
        />
      )}
      {isColumnVisible(columns, 'lotDate') && (
        <Column
          field="lotDate"
          title={t('product.lotDate')}
          cell={props => <DateTimeCell {...props} />}
          width={productListColumnWidths['lotDate']}
        />
      )}
      {isColumnVisible(columns, 'norlabNo') && (
        <Column
          field="norlabNo"
          title={t('product.norlabNo')}
          width={productListColumnWidths['norlabNo']}
        />
      )}
      {isColumnVisible(columns, 'description') && (
        <Column
          field="description"
          title={t('props.description')}
          width={productListColumnWidths['description']}
        />
      )}
      {parameterSet &&
        (parameterSet.parameterSetParameters ?? []).map((item, index) => (
          <Column
            field={`parameter${item.parameterId}`}
            title={`${item.parameterName} - ${item.methodName}`}
            width="120px"
            key={item.id}
            cell={({ dataItem }) => (
              <td>
                {dataItem.parameters[index]?.lessThan === true ? '< ' : ''}
                {getCorrectedValueAtIndexFormatted(dataItem.parameters, index)}
              </td>
            )}
          />
        ))}
    </DataGrid>
  );
};
